import { createContext, useContext, useState } from 'react'
import { createStore, useStore, type ReactStore } from '@w/react-store'
import type { BlockEditorHandle, RemoteSelection } from './types/block-editor.types'
import type { QuestionAnswerValue } from './types/answers.types'
import type { CoreState } from './core/state'
import { sameSelectionAnchors, selectionAnchors, type SelectionAnchors } from './core/selection-anchors'

export interface EditorState {
  /** Keyed by answer part (question/cloze block id), never by qid. */
  answers: Record<string, QuestionAnswerValue>
}

const stateKey = Symbol('editor state')
/** The local selection anchors (cbe-local-selection-anchors-are-observable) live in their own store: a caret move
 * replaces only this root, so answer readers (question, cloze blocks) do not re-render (react-state rule 2). */
const anchorsKey = Symbol('editor selection anchors')
/** Collaborators' selections (cbe-remote-selection-follows-remote-edits) in their own store: a remote caret move
 * re-renders only the remote caret layer, never the editor body or its blocks (react-state rule 2). */
const remoteKey = Symbol('editor remote selections')
type StatefulEditor = BlockEditorHandle & { [stateKey]: ReactStore<EditorState>; [anchorsKey]: ReactStore<SelectionAnchors | null>
  [remoteKey]: ReactStore<readonly RemoteSelection[]> }
export const EditorContext = createContext<BlockEditorHandle | null>(null)

export function withEditorState(
  handle: Omit<BlockEditorHandle, 'setAnswer' | 'clearAnswers' | 'getAnswers' | 'setRemoteSelections'>,
): BlockEditorHandle {
  const store = createStore<EditorState>({ answers: {} })
  const remote = createStore<readonly RemoteSelection[]>([])
  return Object.assign(handle, {
    [remoteKey]: remote,
    setRemoteSelections(selections: readonly RemoteSelection[]) {
      remote.update(() => selections)
    },
    [stateKey]: store,
    [anchorsKey]: createStore<SelectionAnchors | null>(null),
    setAnswer(partId: string, value: QuestionAnswerValue) {
      store.update(state => ({ ...state, answers: { ...state.answers, [partId]: value } }))
    },
    clearAnswers() {
      store.update(state => ({ ...state, answers: {} }))
    },
    getAnswers() {
      return store.getState().answers
    },
  })
}

/** The store `setRemoteSelections` writes; the remote caret layer reads it. */
export function remoteSelectionsStore(editor: BlockEditorHandle): ReactStore<readonly RemoteSelection[]> {
  return (editor as StatefulEditor)[remoteKey]
}

/** Publishes `state`'s selection anchors to the editor's store; an equal value keeps the snapshot. */
export function publishSelectionAnchors(editor: BlockEditorHandle, state: CoreState): void {
  const store = (editor as StatefulEditor)[anchorsKey]
  const next = selectionAnchors(state)
  if (sameSelectionAnchors(store.getState(), next)) return
  store.update(() => next)
}

/** The editor's local text selection as `{anchor, focus}` cursor JSON plus point hints; null for non-text selections. */
export function useEditorSelectionAnchors(editor?: BlockEditorHandle | null): SelectionAnchors | null {
  const contextEditor = useContext(EditorContext)
  const owner = editor === undefined ? contextEditor : editor
  const [pending] = useState(() => createStore<SelectionAnchors | null>(null))
  const anchors = useStore(owner ? (owner as StatefulEditor)[anchorsKey] : pending)
  if (editor === undefined && !owner) throw new Error('useEditor must be used inside a BlockEditor')
  return anchors
}

export function useEditor(): BlockEditorHandle {
  const editor = useContext(EditorContext)
  if (!editor) throw new Error('useEditor must be used inside a BlockEditor')
  return editor
}

// A null handle represents the interval before onReady. Its empty state is local
// to this hook; the mounted editor owns every writable state snapshot.
export function useEditorState<T>(
  select: (state: EditorState) => T,
  editor?: BlockEditorHandle | null,
): T {
  const contextEditor = useContext(EditorContext)
  const owner = editor === undefined ? contextEditor : editor
  const [pending] = useState(() => createStore<EditorState>({ answers: {} }))
  const state = useStore(owner ? (owner as StatefulEditor)[stateKey] : pending)
  if (editor === undefined && !owner) {
    throw new Error('useEditor must be used inside a BlockEditor')
  }
  return select(state)
}
